import { NodeEventsTypes } from '@treedis/tour-loader';
import { useRef, useEffect } from 'react';
import { getClosestSweepFromObject } from '@/components/flows/utils/getClosestSweep';
import { getDetailedRouteDistance } from '@/utils/getDistance';
import { NavigationType } from '../NavigationIntroScreen/components/Popup/NavigationIntroChoicePopup';

export const useDetailedRoutes = ({
  pathFinderLine,
  navigationSettings,
  startingPoint,
  targetLocation,
  allSweeps,
  THREE,
  navType,
}) => {
  const detailedRoutesRef = useRef([]);
  const detailedRoutes = detailedRoutesRef.current;
  const isInitializedRef = useRef(false);

  const resetDetailedRoutes = () => {
    detailedRoutesRef.current = [];
    isInitializedRef.current = false;
    pathFinderLine?.reset();
  };

  const addOrUpdateRoute = (newRoute, isUpdate) => {
    const detailedRoutes = detailedRoutesRef.current;
    if (isUpdate) {
      const existingRouteIndex = detailedRoutes.findIndex((route) => route.id === newRoute.id);
      if (existingRouteIndex !== -1) {
        const existingRoute = detailedRoutes[existingRouteIndex];
        detailedRoutesRef.current[existingRouteIndex] = {
          ...newRoute,
          completed: existingRoute.completed,
        };
        return;
      }
    }
    detailedRoutes.push(newRoute);
  };

  // Track teleport groups to avoid duplicates

  // Process a single waypoint with all complex logic (teleports, mandatory, etc.)
  // This function handles ONE waypoint and calculates distance appropriately
  const updateSingleWaypoint = (path, targetWaypointId, isUpdate = false) => {
    const detailedRoutes = detailedRoutesRef.current;
    const pathDirectionSigns = pathFinderLine.getPathDirectionSigns();
    const teleportPoints = pathFinderLine.getTeleportPoints();

    // Find the target waypoint in the path
    const waypointIndex = path.findIndex((item) => item.data.id === targetWaypointId);
    if (waypointIndex === -1) return; // Waypoint not found in path

    const p = path[waypointIndex];
    const i = waypointIndex;

    let distance: number;
    if (isUpdate) {
      // For updates (when user is moving), calculate from current position (path[0]) to this waypoint
      const pathSegment = path.slice(0, waypointIndex + 1);
      distance = getDetailedRouteDistance(pathSegment, THREE);
    } else {
      // For initial setup, calculate distance between consecutive waypoints in detailedRoutes
      if (i === 0) {
        distance = 0; // Starting point has no distance
      } else {
        // Find the previous waypoint in the detailedRoutes array (not path array)
        const detailedRoutes = detailedRoutesRef.current;

        // Find the last waypoint that was added to detailedRoutes
        let prevWaypointPathIndex = 0; // Start from beginning if no previous waypoint found

        if (detailedRoutes.length > 0) {
          // Find the path index of the last waypoint in detailedRoutes
          const lastDetailedRoute = detailedRoutes[detailedRoutes.length - 1];
          const lastWaypointPathIndex = path.findIndex(
            (pathItem) =>
              pathItem.data.id === lastDetailedRoute.id ||
              (lastDetailedRoute.isFinish && pathItem.data.id === targetLocation?.id),
          );

          if (lastWaypointPathIndex !== -1) {
            prevWaypointPathIndex = lastWaypointPathIndex;
          }
        }

        // Calculate distance from the previous waypoint to current waypoint
        const pathSegment = path.slice(prevWaypointPathIndex, waypointIndex + 1);
        distance = getDetailedRouteDistance(pathSegment, THREE);
      }
    }
    // Check if this is a direction sign
    const foundPathSign = pathDirectionSigns.find((d) => d.id === p.data.id);
    if (foundPathSign) {
      const foundTpGroup = teleportPoints.find((tp) =>
        tp.pointNodes?.find((tpn) => tpn?.sweep === p.data.id),
      );
      if (!foundTpGroup) {
        addOrUpdateRoute(
          {
            ...foundPathSign,
            distance: distance.toFixed(1),
          },
          isUpdate,
        );
        return;
      }
    }

    // Check if this is a teleport point
    if (teleportPoints.length > 0 && navType !== NavigationType.NoStairs) {
      const foundTpGroup = teleportPoints
        .filter(
          (tp) =>
            (tp.type === 'elevator' && navType === NavigationType.Shortest) ||
            (tp.type === 'stairway' && navType === NavigationType.NoElevators),
        )
        .find((tp) => {
          return tp.pointNodes?.find((tpn) => tpn?.sweep === p.data.id);
        });

      if (foundTpGroup) {
        const startingTpNode = foundTpGroup.pointNodes.filter(
          (tp) => tp.sweep === path[i - 1]?.data.id,
        );
        const endTpNode = foundTpGroup.pointNodes.filter((tp) => tp.sweep === p.data.id);
        const endTpTurnSign = pathDirectionSigns.find((d) => {
          return d.id === endTpNode[0]?.sweep;
        });

        if (!!startingTpNode?.length && !!endTpNode?.length) {
          const pathSegment = path.slice(0, waypointIndex - 1);
          distance = getDetailedRouteDistance(pathSegment, THREE);
          addOrUpdateRoute(
            {
              ...p.data,
              tpGroupStart: { ...startingTpNode[0], type: foundTpGroup.type },
              distance: distance.toFixed(1),
              tpGroupEnd: { ...endTpNode[0], type: foundTpGroup.type, turn: endTpTurnSign?.turn },
            },
            isUpdate,
          );
          return;
        }
      }
    }

    // Check if this is a mandatory waypoint
    if (p.data.isMandatory) {
      addOrUpdateRoute(
        {
          ...p.data,
          distance: distance.toFixed(1),
        },
        isUpdate,
      );
      return;
    }

    // Check if this is the finish point
    if (i === path.length - 2) {
      //-1 for starting point
      const finishAlreadyExists = detailedRoutes.some(
        (route) => route.isFinish && route.id === targetLocation?.id,
      );
      if (!finishAlreadyExists || isUpdate) {
        addOrUpdateRoute(
          { ...targetLocation, distance: distance.toFixed(1), isFinish: true },
          isUpdate,
        );
      }
    }
  };

  // Update only the active waypoint's distance
  const updateActiveWaypointDistance = (path) => {
    const detailedRoutes = detailedRoutesRef.current;

    // Find the next uncompleted waypoint
    const activeWaypoint = detailedRoutes.find((route) => !route.completed);
    if (!activeWaypoint || !path.length) return;

    // Use the same single waypoint function to update just this waypoint
    updateSingleWaypoint(path, activeWaypoint.id, true);
  };

  // Subscribe to events only once using useEffect
  useEffect(() => {
    if (!pathFinderLine) return;

    const handlePathRenderComplete = (data) => {
      const { detailedRouteEnabled } = navigationSettings;
      const { path } = data;

      // Only build routes once on initial setup
      if (
        detailedRouteEnabled &&
        !isInitializedRef.current &&
        !!path.length &&
        startingPoint &&
        targetLocation
      ) {
        // Clear existing routes to prevent duplicates
        detailedRoutesRef.current = [];

        // Add starting point
        let detail = null;
        if (startingPoint.tag) {
          const { closestSweep } = getClosestSweepFromObject({
            allSweeps,
            target: startingPoint.tag,
          });
          detail = { ...closestSweep, tag: startingPoint.tag };
        } else {
          detail = startingPoint;
        }
        addOrUpdateRoute(detail, false);

        // Loop through path and call updateSingleWaypoint for each waypoint
        // Let updateSingleWaypoint handle all the complex logic internally
        path.forEach((p: any) => {
          updateSingleWaypoint(path, p.data.id, false);
        });

        // Mark as initialized after building routes
        isInitializedRef.current = true;
      }
    };

    const handleNavigationComplete = () => {
      resetDetailedRoutes();
    };

    // Subscribe to navigation events
    pathFinderLine.on(NodeEventsTypes.NAVIGATION_PATH_RENDER_COMPLETE, handlePathRenderComplete);
    pathFinderLine.on(NodeEventsTypes.NAVIGATION_COMPLETE, handleNavigationComplete);

    // Cleanup function
    return () => {
      pathFinderLine.off(NodeEventsTypes.NAVIGATION_PATH_RENDER_COMPLETE, handlePathRenderComplete);
      pathFinderLine.off(NodeEventsTypes.NAVIGATION_COMPLETE, handleNavigationComplete);
    };
  }, [
    pathFinderLine,
    navigationSettings,
    startingPoint,
    targetLocation,
    allSweeps,
    THREE,
    navType,
  ]);

  // Subscribe to sweep changes for distance updates
  useEffect(() => {
    if (!pathFinderLine?.mpSDK || !isInitializedRef.current) return;

    const handleSweepChange = (currentSweep: any) => {
      const detailedRoutes = detailedRoutesRef.current;
      if (!detailedRoutes.length || !pathFinderLine?.component?.inputs?.path?.length) return;

      const path = pathFinderLine.component.inputs.path;

      // Mark completed if reached
      detailedRoutesRef.current.forEach((route) => {
        if (route.completed) return;
        if (route.id === currentSweep.sid || route.id === currentSweep.id) {
          route.completed = true;
          route.distance = 0;
        }
      });

      // Update only the active waypoint's distance
      updateActiveWaypointDistance(path);
    };

    const sweepSubscription = pathFinderLine.mpSDK.Sweep.current.subscribe(handleSweepChange);

    // Cleanup function
    return () => {
      if (sweepSubscription && typeof sweepSubscription.unsubscribe === 'function') {
        sweepSubscription.unsubscribe();
      }
    };
  }, [pathFinderLine, isInitializedRef.current]);

  return { detailedRoutes, resetDetailedRoutes };
};
